import { useRef } from "react";
import { revalidateLogic, useForm } from "@tanstack/react-form";
import { z } from "zod";
import { sendFormEvent, sendLeadGenerationEvent } from "../ga4/eventHandler";

const MAX_FIELD_LENGTH = 50;
const MAX_EMAIL_LENGTH = 150;

const CONTACT_METHOD_ERROR = "Office Phone Number or Email Address is required";

const optionalTextField = (fieldName: string) =>
  z
    .string()
    .trim()
    .max(MAX_FIELD_LENGTH, `${fieldName} must not exceed 50 characters`);

const requestDemoFormSchema = z
  .object({
    origin: optionalTextField("Origin"),
    company: optionalTextField("Company name"),
    companyWebsiteUrl: z.string().trim(),
    address: optionalTextField("Address"),
    address2: optionalTextField("Address 2"),
    city: optionalTextField("City"),
    state: optionalTextField("State"),
    zip: optionalTextField("Zip"),
    country: optionalTextField("Country"),
    website: optionalTextField("Website"),
    fname: optionalTextField("Full name"),
    title: optionalTextField("Job Position"),
    phone: optionalTextField("Office Phone Number"),
    mphone: optionalTextField("Mobile Number"),
    email: z
      .string()
      .trim()
      .max(MAX_EMAIL_LENGTH, "Email address must not exceed 150 characters")
      .refine((value) => !value || z.email().safeParse(value).success, {
        message: "Invalid email address",
      }),
    numLocs: optionalTextField("Number of Locations"),
    comment: z.string().trim(),
    current: z.number(),
    newsLetter: z.number(),
    turnstileToken: z
      .string()
      .trim()
      .min(1, "Please complete the verification challenge")
      .max(2048),
  })
  .superRefine((value, ctx) => {
    if (value.phone || value.email) {
      return;
    }

    ctx.addIssue({
      code: "custom",
      path: ["phone"],
      message: CONTACT_METHOD_ERROR,
    });

    ctx.addIssue({
      code: "custom",
      path: ["email"],
      message: CONTACT_METHOD_ERROR,
    });
  });

export type RequestDemoFormValues = z.infer<typeof requestDemoFormSchema>;

export function useRequestDemoForm({
  onSubmit,
}: {
  onSubmit: (data: RequestDemoFormValues) => Promise<{
    success: boolean;
    message?: string;
  }>;
}) {
  const formStartedRef = useRef(false);

  const form = useForm({
    formId: "request-demo",
    defaultValues: {
      origin: "KO_Website",
      company: "",
      address: "",
      address2: "",
      city: "",
      state: "",
      zip: "",
      country: "",
      website: "",
      companyWebsiteUrl: "",
      fname: "",
      title: "",
      phone: "",
      mphone: "",
      email: "",
      numLocs: "",
      comment: "",
      current: 0,
      newsLetter: 0,
      turnstileToken: "",
    } satisfies RequestDemoFormValues,
    validationLogic: revalidateLogic(),
    validators: {
      onDynamic: requestDemoFormSchema,
    },
    listeners: {
      onChange: () => {
        if (!formStartedRef.current) {
          formStartedRef.current = true;
          sendFormEvent("DEMO_REQUEST", "START");
        }
      },
    },
    onSubmit: async ({ value }) => {
      sendFormEvent("DEMO_REQUEST", "SUBMIT");

      try {
        const request = await onSubmit(value);

        if (!request.success) {
          throw new Error();
        }

        sendLeadGenerationEvent("DEMO_REQUEST");

        form.reset();
        formStartedRef.current = false;
      } catch {
        sendFormEvent("DEMO_REQUEST", "FAILED");
      }
    },
  });

  return {
    form,
  };
}
